import { useState, useCallback, useMemo } from 'react';
import { APARTMENT_GROUP, BUILDING } from '../../../utils/Constants';
import {
  calculateIndoorTotal,
  calculateOutdoorTotal,
} from '../../../utils/Calculations';

const useInProgress = () => {
  // State for apartments list and initialization status
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Combined filter state to minimize the number of state variables
  const [filters, setFilters] = useState(() => ({
    area: [19, 80] as [number, number],
    building: null as string | null,
    roomTypes: [] as string[],
    floor: null as string | null,
    spaceTypes: [] as string[],
    availability: null as string | null,
  }));

  // Other UI states
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  const [openApartmentModal, setOpenApartmentModal] = useState<boolean>(false);
  const [openBuildingModal, setOpenBuildingModal] = useState<boolean>(false);
  const [openAnimation, setOpenAnimation] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [activeApartment, setActiveApartment] = useState<Apartment | null>(
    null
  );

  // Handler for updating filters in a functional way to avoid unnecessary re-renders
  const updateFilter = useCallback((key: keyof typeof filters, value: any) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  }, []);

  // Handlers for individual filter updates
  const handleBuildingFilter = useCallback(
    (building: string) =>
      updateFilter('building', filters.building === building ? null : building),
    [filters.building, updateFilter]
  );

  const handleFloorFilter = useCallback(
    (floor: string | null) => updateFilter('floor', floor),
    [updateFilter]
  );

  const handleRoomTypeFilter = useCallback((roomType: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      roomTypes: prevFilters.roomTypes.includes(roomType)
        ? prevFilters.roomTypes.filter((r) => r !== roomType)
        : [...prevFilters.roomTypes, roomType],
    }));
  }, []);

  const handleSpaceTypeFilter = useCallback((spaceType: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      spaceTypes: prevFilters.spaceTypes.includes(spaceType)
        ? prevFilters.spaceTypes.filter((s) => s !== spaceType)
        : [...prevFilters.spaceTypes, spaceType],
    }));
  }, []);

  const handleAvailabilityFilter = useCallback(
    (availability: string | null) => updateFilter('availability', availability),
    [updateFilter]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({
      area: [19, 80],
      building: null,
      roomTypes: [],
      floor: null,
      spaceTypes: [],
      availability: null,
    });
    setOpenAnimation(false)
  }, []);

  // Filter apartments based on current filter state, only if initialized
  const filterApartments = useMemo(() => {
    if (!isInitialized) {
      return [];
    }

    return apartments.filter((apartment) => {
      const totalArea =
        calculateIndoorTotal(apartment) + calculateOutdoorTotal(apartment);
      const { area, building, roomTypes, floor, spaceTypes, availability } =
        filters;

      const meetsBuildingCriteria =
        !building ||
        (building === BUILDING.A
          ? apartment.building === building
          : apartment.building !== BUILDING.A);

      const meetsRoomCriteria =
        roomTypes.length === 0 || roomTypes.includes(apartment.name);

      const meetsSpaceTypeCriteria =
        spaceTypes.length === 0 ||
        (spaceTypes.includes(APARTMENT_GROUP.BP)
          ? apartment.bp
          : spaceTypes.includes(apartment.floor));

      const meetsAvailabilityCriteria =
        !availability ||
        availability === '0' ||
        apartment.availability === availability;

      const meetsAreaCriteria = totalArea >= area[0] && totalArea <= area[1];

      const meetsFloorCriteria = !floor || apartment.floor === floor;

      return (
        meetsBuildingCriteria &&
        meetsRoomCriteria &&
        meetsSpaceTypeCriteria &&
        meetsAvailabilityCriteria &&
        meetsAreaCriteria &&
        meetsFloorCriteria
      );
    });
  }, [isInitialized, apartments, filters]);

  return {
    openAnimation,
    setOpenAnimation,
    setApartments,
    isInitialized,
    setIsInitialized,
    filters,
    setFilters,
    hoveredArea,
    setHoveredArea,
    openApartmentModal,
    setOpenApartmentModal,
    openBuildingModal,
    setOpenBuildingModal,
    openFilter,
    setOpenFilter,
    activeApartment,
    setActiveApartment,
    handleBuildingFilter,
    handleFloorFilter,
    handleRoomTypeFilter,
    handleSpaceTypeFilter,
    handleAvailabilityFilter,
    filterApartments,
    clearAllFilters,
  };
};

export default useInProgress;
